import { usePermissions } from '@/contexts/PermissionsContext';
import {
  PermissionKey,
  PERMISSIONS,
  hasAnyPermission,
} from '@/types/permissions';

/**
 * Hook to check if user has access to specific permissions
 * @param requiredPermissions - Array of permission keys to check
 * @returns boolean - true if user has ANY of the required permissions
 */
export function usePermissionAccess(
  requiredPermissions: PermissionKey[]
): boolean {
  const { permissions } = usePermissions();
  return hasAnyPermission(permissions, requiredPermissions);
}

/**
 * Hook to check if user has access to all specified permissions
 * @param requiredPermissions - Array of permission keys to check
 * @returns boolean - true if user has ALL of the required permissions
 */
export function usePermissionAccessAll(
  requiredPermissions: PermissionKey[]
): boolean {
  const { permissions } = usePermissions();
  return requiredPermissions.every(permission => permissions[permission] === 1);
}

/**
 * Hook to check route access based on route permissions mapping
 * @param routePath - Route path to check
 * @returns boolean - true if user has access to the route
 */
export function useRouteAccess(routePath: string): boolean {
  const { permissions } = usePermissions();

  // Route permissions mapping
  const routePermissions: Record<string, PermissionKey[]> = {
    '/file-upload': [PERMISSIONS.FILES_ACCESS],
    '/data-import': [PERMISSIONS.DATA_ACCESS],
    '/csv-upload': [PERMISSIONS.CSV_ACCESS],
    '/audit-trail': [PERMISSIONS.AUDIT_TRAIL_ACCESS],
    '/study-files': [PERMISSIONS.STUDY_FILES_ACCESS],
    '/whitelist-management': [PERMISSIONS.WHITELIST_ACCESS],
  };

  const requiredPermissions = routePermissions[routePath];

  if (!requiredPermissions) {
    return true; // Allow access to routes without specific permissions
  }

  return hasAnyPermission(permissions, requiredPermissions);
}
